/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-autofocus */
import React, { useEffect, useRef } from "react"
import { PropTypes } from "prop-types"
import { connect } from "react-redux"
import { IssueManagementSelect } from "selects/containers/PieSelect"

import { issueAdderConnection } from "shared/profiles/issueAdder/selectors"
import { getIssues, removeFromIssue, addToIssue } from "shared/profiles/issueAdder/action-creators"
import { removeFromIssueAndMoveUp } from "app/static/frontend/components/IssueAdder/action-creators"
import { loadIssueAlignments, postIssueAlignment, removeIssueAlignment } from "shared/stakeholder/action-creators"
import { segueToProfileWithId } from "global-action-creators"

import Icon from "app/static/frontend/components/Icon"
import "app/static/stylus/positionAdder/positionAdder.styl"
import { useOnClickOutside, usePrevious } from "app/static/frontend/imports/hooks"

import withImmutablePropsToJS from "with-immutable-props-to-js"

import { SelectWithRemovableValues, WorkflowComponent, WorkflowStrip } from "quorumdesign"
import { sendUserActionPost } from "shared/user-actions/user-actions-action-creators"

export const UnconnectedIssueAdder = (props) => {
    const issueAdderRef = useRef()
    useOnClickOutside(issueAdderRef, (e) => {
        if (props.profileId && props.profileId.toString() !== e.target.id && props.handleClickOutside) {
            props.handleClickOutside()
        }
    })

    const prevProps = usePrevious(props)
    useEffect(() => {
        if (props.profileId && prevProps?.profileId !== props.profileId) {
            fetchData()
        }
    }, [props, prevProps])

    const supportsAlignment = () =>
        [
            DjangIO.app.models.QuorumDataType.person.value,
            DjangIO.app.models.QuorumDataType.staffer.value,
            DjangIO.app.models.QuorumDataType.supporter.value,
        ].includes(props.dataType.value)

    const fetchData = async () => {
        await props.getIssues({
            qdt: props.dataType,
            id: props.profileId,
        })
        if (supportsAlignment() && props.issues && props.issues.length) {
            props.loadIssueAlignments({
                qdt: props.dataType,
                profileId: props.profileId,
                issueIds: props.issues.map((issue) => issue.id),
            })
        }
    }

    const assignIssue = (issue) => {
        props.addToIssue({
            qdt: props.dataType,
            id: props.profileId,
            issue,
            region: props.region,
        })

        if (props.dataType.value === DjangIO.app.models.QuorumDataType.bill.value) {
            sendUserActionPost({
                userActionType: DjangIO.app.useractions.types.UserActionType.set_issue_on_bills,
                description: `Issue changed on bills`,
                quorumDataType: DjangIO.app.models.QuorumDataType.bill.value,
            })
        } else if (props.dataType.value === DjangIO.app.models.QuorumDataType.document.value) {
            sendUserActionPost({
                userActionType: DjangIO.app.useractions.types.UserActionType.set_issue_on_documents,
                description: `Issue changed on documents`,
                quorumDataType: DjangIO.app.models.QuorumDataType.document.value,
            })
        } else if (props.dataType.value === DjangIO.app.models.QuorumDataType.regulation.value) {
            sendUserActionPost({
                userActionType: DjangIO.app.useractions.types.UserActionType.set_issue_on_regulations,
                description: `Issue changed on regulation`,
                quorumDataType: DjangIO.app.models.QuorumDataType.regulation.value,
            })
        }
    }

    const unassignIssue = (issue) =>
        props.removeFromIssueAndMoveUp({
            qdt: props.dataType,
            id: props.profileId,
            issue,
            region: props.region,
        })

    const addAlignment = (issue, alignmentType) => {
        props.postIssueAlignment({
            qdt: props.dataType,
            profileId: props.profileId,
            issueId: issue.id,
            alignmentTypeId: alignmentType.id,
        })
    }

    const removeAlignment = (issue, alignmentType) => {
        props.removeIssueAlignment({
            qdt: props.dataType,
            profileId: props.profileId,
            issueId: issue.id,
            alignmentTypeId: alignmentType.id,
        })
    }

    const renderIssue = (issue) => (
        <div className="position-item" role="listitem">
            <span
                role="button"
                tabIndex="-1"
                onClick={() =>
                    props.segueToProfileWithId(issue.id, DjangIO.app.models.QuorumDataType.issue_management.value)
                }
            >
                {issue.parent && issue._extra.parent_name ? `${issue.name} (${issue._extra.parent_name})` : issue.name}
            </span>
            <Icon lg className="label-icon" name="minus-circle" onClick={() => unassignIssue(issue)} />
        </div>
    )

    const renderIssues = () => (
        <div className="choices" role="list">
            {props.issues && props.issues.length ? (
                props.issues.map((each) => renderIssue(each))
            ) : (
                <ul className="quorum-selected-objects no-results">
                    <li className="quorum-tag loading blue-tag-1 no-results">
                        <h4>{props.noResultsType} not attached to any issues</h4>
                    </li>
                </ul>
            )}
        </div>
    )

    // eslint-disable-next-line react/sort-comp
    const getIssueAlignmentTypeId = (issue) => props.issueAlignments && props.issueAlignments[issue.id]

    const getAlignmentActions = (issue, issueAlignmentTypeId) =>
        props.alignmentTypes &&
        props.alignmentTypes.sort(DjangIO.valueSortDescending).map((type) => ({
            id: type.id,
            icon: type.icon,
            iconFamily: type.icon_family,
            iconTitle:
                issueAlignmentTypeId === type.id
                    ? `Remove Alignment of ${type.label}`
                    : `Set Alignment of ${type.label}`,
            isActive: issueAlignmentTypeId === type.id,
            onClickFn: () =>
                issueAlignmentTypeId === type.id ? removeAlignment(issue, type) : addAlignment(issue, type),
        }))

    const getActiveAlignmentIcon = (issueAlignmentTypeId) => {
        if (props.alignmentTypes) {
            const { icon, icon_family, label } =
                props.alignmentTypes.find((aType) => aType.id === issueAlignmentTypeId) || {}
            return [icon, icon_family, label]
        }
        return []
    }

    const renderIssueStrips = () =>
        props.issues &&
        props.issues.length &&
        props.issues.map((issue) => {
            const ancestorIds = props.ancestorIds[issue.id]
            const parentName = props.parentNames[issue.id]

            const isUpdating = props.updatingAlignments && props.updatingAlignments.includes(`${issue.id}`)
            const alignmentTypeId = getIssueAlignmentTypeId(issue)
            const alignmentActions = supportsAlignment() ? getAlignmentActions(issue, alignmentTypeId) : []
            const [activeIcon, activeIconFamily, activeIconTitle] = getActiveAlignmentIcon(alignmentTypeId)

            return (
                <WorkflowStrip
                    hoverActions={[
                        {
                            text: issue._isLeaf ? "Remove" : "Remove All",
                            icon: "trash-alt",
                            iconFamily: "fas",
                            onClickFn: () => unassignIssue(issue),
                        },
                        ...alignmentActions,
                    ]}
                    key={issue.id}
                    icon={parentName ? "level-up" : "thumbtack"}
                    iconColor={parentName ? "secondary" : "primary"}
                    iconFamily={parentName ? "fal" : "far"}
                    iconRotation={parentName ? 90 : 0}
                    // indent only 2nd level and deeper sub-issues
                    indent={ancestorIds.length > 2 && ancestorIds.length - 2}
                    onClickFn={() =>
                        props.segueToProfileWithId(issue.id, DjangIO.app.models.QuorumDataType.issue_management.value)
                    }
                    text={issue.name}
                    textType={parentName ? "minor" : "major"}
                    rightIcon={activeIcon}
                    rightIconFamily={activeIconFamily}
                    rightIconTitle={`Current Alignment is ${activeIconTitle}`}
                    isLoading={props.isLoadingAlignments || isUpdating}
                />
            )
        })

    return !props.inline ? (
        <WorkflowComponent
            emptyButton={
                !props.loading && {
                    text: "Add Issue",
                    icon: "plus",
                    shouldOpenPopUp: true,
                }
            }
            emptyText={props.loading ? "Loading issues..." : "No issues currently"}
            iconTooltipText="Add"
            title="Add to Issue"
            popUpComponent={
                <IssueManagementSelect
                    isDesignSystem
                    onChange={assignIssue}
                    value={0}
                    defaultMenuIsOpen
                    autoFocus
                    isFocused
                    requiresBaseComponent
                    // we need to have the ancestors dehydrate extra in order to populate
                    // the issue adder with super-issues of newly added issues
                    permanentFilter={{ dehydrate_extra: ["parent_name", "ancestors"] }}
                />
            }
        >
            {renderIssueStrips()}
        </WorkflowComponent>
    ) : (
        <React.Fragment>
            {
                <SelectWithRemovableValues
                    clickOutsideRef={issueAdderRef}
                    dataCy="issues-search-table-column"
                    SelectComponent={
                        <IssueManagementSelect
                            isDesignSystem
                            onChange={assignIssue}
                            value={0}
                            defaultMenuIsOpen
                            autoFocus
                            isFocused
                            requiresBaseComponent
                            permanentFilter={{ dehydrate_extra: ["parent_name", "ancestors"] }}
                        />
                    }
                    values={props.issues}
                    valueStripOnClick={(issue) =>
                        props.segueToProfileWithId(issue.id, DjangIO.app.models.QuorumDataType.issue_management.value)
                    }
                    valueStripRemove={(issue) => unassignIssue(issue)}
                />
            }
        </React.Fragment>
    )
}

UnconnectedIssueAdder.propTypes = {
    // Functions
    addToIssue: PropTypes.func.isRequired,
    loadIssueAlignments: PropTypes.func.isRequired,
    getIssues: PropTypes.func.isRequired,
    handleClickOutside: PropTypes.func,
    postIssueAlignment: PropTypes.func.isRequired,
    removeFromIssue: PropTypes.func.isRequired,
    removeFromIssueAndMoveUp: PropTypes.func.isRequired,
    removeIssueAlignment: PropTypes.func.isRequired,
    segueToProfileWithId: PropTypes.func.isRequired,
    // Props
    issueAlignments: PropTypes.object,
    alignmentTypes: PropTypes.array,
    ancestorIds: PropTypes.object,
    dataType: PropTypes.object.isRequired,
    inline: PropTypes.bool,
    isLoadingAlignments: PropTypes.bool,
    issues: PropTypes.array,
    updatingAlignments: PropTypes.array,
    loading: PropTypes.bool,
    noResultsType: PropTypes.string,
    parentNames: PropTypes.object,
    profileId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    region: PropTypes.number,
}

export default connect(issueAdderConnection, {
    getIssues,
    removeFromIssue,
    removeFromIssueAndMoveUp,
    addToIssue,
    segueToProfileWithId,
    loadIssueAlignments,
    postIssueAlignment,
    removeIssueAlignment,
})(withImmutablePropsToJS(UnconnectedIssueAdder))
